import { Injectable, inject } from "@angular/core";
import { ObjectUtils } from "~app/common/utils/object-utils";
import { EventService } from "../../../services/cqs/event.service";
import { MapDeselectedEvent, MapSelectedEvent } from "../map.events";
import { IHitItem } from "../map.models";
import { IMapDrawLayer, IMapGeometry, IMapSettings } from "../map.settings";
import { Symbols } from "../symbols/symbol.styles";
import { MapFreehandService } from "./map-freehand.service";
import { SpatialService } from "./spatial.service";
import { SymbolService } from "./symbol.service";

export interface IMapService {
    init(mapsettings: IMapSettings, arcView: any, arcDraw: any, arcQueryTask: any);
}

@Injectable({
    providedIn: "root"
})
export class MapSelectService implements IMapService {
    arcDraw: any;
    arcView: any;
    isSelecting: boolean;
    mapsettings: IMapSettings;

    private readonly eventService = inject(EventService);
    private readonly freehandService = inject(MapFreehandService);
    private readonly spatialService = inject(SpatialService);
    private readonly symbolService = inject(SymbolService);

    canSelect(): boolean {
        return this.mapsettings?.drawLayers?.some(x => x.selectable && x.geometries?.length > 0);
    }

    deselect(layerid: string, geometries: IMapGeometry[], signal = true): void {
        const arcgisLayer = this.getArcgisLayer(layerid);
        if (!ObjectUtils.hasValue(arcgisLayer)) {
            return;
        }
        const layer = this.getLayer(layerid);
        const parents = [];
        geometries.forEach(geometry => {
            if (ObjectUtils.isNullOrUndefined(geometry.parent)) {
                geometry.selected = false;
                parents.push(geometry);
            }
        });
        const graphics = arcgisLayer.graphics.items.filter(gfx => geometries.some(g => g.id === gfx.attributes.id));
        this.deselectGraphics(graphics);
        if (signal) {
            if (layer.onDeselect) {
                layer.onDeselect(parents);
            }
            const event = new MapDeselectedEvent(parents, layer.id);
            this.eventService.emit(event);
        }
    }

    getSelectable(): IMapGeometry[] {
        const result = [];
        const layers = this.mapsettings?.drawLayers?.filter(x => x.selectable && x.geometries?.length > 0);
        layers?.forEach(layer => {
            layer.geometries.forEach(g => {
                result.push(g);
            });
        });
        return result;
    }

    getSelected(): IMapGeometry[] {
        const layers = this.mapsettings?.drawLayers?.filter(x => x.selectable && x.geometries?.length > 0);
        return this.getSelectedGeometriesOnLayers(layers);
    }

    getSelectedOnLayer(layerid: string): IMapGeometry[] {
        const layers = this.mapsettings?.drawLayers?.filter(x => x.id === layerid && x.selectable && x.geometries?.length > 0);
        return this.getSelectedGeometriesOnLayers(layers);
    }

    hasSelectable(): boolean {
        return this.canSelect() && this.mapsettings?.drawLayers?.filter(x => x.selectable)?.some(x => x.geometries?.length > 0);
    }

    hasSelection(): boolean {
        return this.canSelect() && this.mapsettings?.drawLayers?.filter(x => x.selectable)?.some(x => x.geometries?.some(g => g.selected));
    }

    hasSelectionOnLayer(layerid: string): boolean {
        return this.canSelect() && this.mapsettings?.drawLayers?.filter(x => x.id === layerid && x.selectable)?.some(x => x.geometries?.some(g => g.selected));
    }

    init(mapsettings: IMapSettings, arcView: any, arcDraw: any, arcQueryTask: any) {
        this.mapsettings = mapsettings;
        this.arcView = arcView;
    }

    initDraw(arcDraw: any) {
        this.arcDraw = arcDraw;
    }

    isReady(): boolean {
        return this.mapsettings && this.arcView;
    }

    removeAllSelections(): void {
        if (this.arcDraw) {
            this.arcDraw.reset(true);
            this.isSelecting = false;
        }
        this.selectAll(false);
    }

    select(layerid: string, geometries: IMapGeometry[], signal = true): void {
        const arcgisLayer = this.getArcgisLayer(layerid);
        if (!ObjectUtils.hasValue(arcgisLayer)) {
            return;
        }
        const layer = this.getLayer(layerid);
        const parents = [];

        geometries.forEach(geometry => {
            if (geometry.visible !== true) {
                console.error("Tried to select invisible geometry", geometry);
            } else {
                if (ObjectUtils.isNullOrUndefined(geometry.parent)) {
                    geometry.selected = true;
                    parents.push(geometry);
                }
                const graphics = arcgisLayer.graphics.find(x => x.attributes.id === geometry.id);
                this.selectGraphic(graphics, Symbols.Styles.selected);
            }
        });
        if (signal) {
            if (layer.onSelect) {
                layer.onSelect(parents);
            }
            const event = new MapSelectedEvent(parents, layer.id);
            this.eventService.emit(event);
        }
    }

    selectAll(state = true): void {
        const layers = this.mapsettings.drawLayers?.filter(x => x.selectable === true && x.visible);
        layers.forEach(layer => {
            const candidates = layer.geometries.filter(x => x.selected !== state);
            if (state) {
                this.select(layer.id, candidates);
            } else {
                this.deselect(layer.id, candidates);
            }
        });
    }

    startLassoSelect(onFinish: () => void): void {
        if (this.isSelecting) {
            return;
        }
        this.isSelecting = true;
        this.freehandService.start((freehandGeometry) => {
            this.arcView.map.layers.forEach(arcLayer => {
                const rasklayer = this.getLayer(arcLayer.id);
                if (rasklayer && rasklayer.visible && rasklayer.selectable) {
                    const arcPolygon = this.spatialService.toArcGis(freehandGeometry);
                    const foundArcGraphics = this.spatialService.findGeometriesWithin(arcLayer, arcPolygon);
                    const foundGeoids = foundArcGraphics.map(g => g.attributes?.geoid);
                    // eslint-disable-next-line sonarjs/no-nested-functions
                    const geometries = rasklayer.geometries.filter(g => !g.parent && foundGeoids.some(h => h === g.geoid));
                    this.select(arcLayer.id, geometries);
                }
                onFinish();
                this.isSelecting = false;
            });
        });
    }

    stopLassoSelect(): void {
        this.freehandService.stop();
        this.isSelecting = false;
    }

    toggle(items: IHitItem[]): void {
        items.filter(x => x.layer.selectable === true).forEach(item => {
            if (item.geometry.selected) {
                this.deselect(item.layer.id, [item.geometry]);
            } else {
                this.select(item.layer.id, [item.geometry]);
            }
        });
    }

    private deselectGraphics(deselectionGraphics: any[]) {
        deselectionGraphics?.forEach(graphic => {
            const outline = Symbols.Styles.default;
            this.selectGraphic(graphic, outline, false);
        });
    }

    private getArcgisLayer(id: string) {
        return this.getArcgisLayerView(id)?.layer;
    }

    private getArcgisLayerView(id: string) {
        return this.arcView.allLayerViews.find(x => x.layer.id === id);
    }

    private getLayer(layerid: string) {
        return this.mapsettings.drawLayers.find(l => l.id === layerid);
    }

    private getSelectedGeometriesOnLayers(layers: IMapDrawLayer[] | undefined): IMapGeometry[] {
        const result: IMapGeometry[] = [];
        layers?.forEach(layer => {
            const gs = layer.geometries?.filter(g => g.selected);

            gs.forEach(g => {
                result.push(g);
            });
        });
        return result;
    }

    private selectGraphic(graphic: any, outline: any, selected = true) {
        let symbol;
        if (ObjectUtils.isNullOrUndefined(graphic)) {
            console.error("selectGraphic: null or undefined graphic!");
            return;
        }
        if (ObjectUtils.isNullOrUndefined(graphic.symbol)) {
            console.error("selectGraphic: graphic.symbol missing!", graphic);
        } else {
            if (graphic.symbol.url) {
                symbol = this.symbolService.fromIcon(this.mapsettings.icons.find(x => x.id === graphic.symbol.attributes.id), selected);
            } else {
                symbol = this.symbolService.cloneSymbol(graphic.symbol);
                symbol.outline = outline;
            }
            graphic.symbol = symbol;
        }
        graphic.setAttribute("selected", selected);
    }
}
